/**
 * Per-session in-memory cache for document template HTML bodies.
 *
 * The two fixed document templates (agency campaign proposal and
 * influencer × host campaign contract) are fetched once per browser
 * session by the PDF generators and reused on subsequent calls. The
 * cache lives in module state — it is cleared on full page reload,
 * and explicitly via `clearTemplateCache()` after the admin editor
 * saves a new version so the next PDF generation in the same session
 * picks up the updated content.
 *
 * No persistence (localStorage / sessionStorage) is used. [Req 8.1,
 * 8.2, 9.1, 9.2; design §6.2]
 */

import type { DocType } from '../constants/documentTemplatePlaceholders';

interface CacheEntry {
  content: string;
  fetchedAt: number;
}

const cache = new Map<DocType, CacheEntry>();

/**
 * Return the cached template content for `docType`, or `undefined`
 * if nothing has been cached yet in this session.
 *
 * Generators use the `undefined` sentinel to decide whether to fetch
 * from Apollo (see design §2.2 and §6.2).
 */
export function getCachedTemplate(docType: DocType): string | undefined {
  return cache.get(docType)?.content;
}

/**
 * Store `content` under `docType`, stamping `fetchedAt` with the
 * current epoch millis. Overwrites any existing entry for the same
 * doc type.
 */
export function setCachedTemplate(docType: DocType, content: string): void {
  cache.set(docType, { content, fetchedAt: Date.now() });
}

/**
 * Drop all cached entries. Invoked by the admin editor's save-success
 * flow so the admin's own session picks up the new template on its
 * next PDF generation.
 */
export function clearTemplateCache(): void {
  cache.clear();
}
